import { useEffect } from 'react';
import { reportLeak, type LeakKind } from '@/lib/telemetry';

/**
 * The leak detector.
 *
 * This is not a fallback renderer. It is the component that fires when
 * something reached a client surface that the client surface does not know how
 * to draw - an unmapped stream kind, an unmapped block type, an author kind
 * that should be impossible here.
 *
 * The server is authoritative: `/client/**` physically cannot return an expert
 * turn (I7) and never names the agent vendor (I8). So anything landing here is
 * evidence that a filter missed something. That has to be **impossible to miss
 * during review**, which is why development gets a loud slab of alarm colour
 * rather than a tasteful grey box.
 *
 * In production it paints a neutral placeholder and reports. It never renders
 * the unrecognised payload, because the entire reason we are here is that we do
 * not know whether that payload is safe to show.
 */

interface UnknownTurnProps {
  /** What kind of thing was unmapped. */
  leakKind: LeakKind;
  /** A short machine token, e.g. the unmapped kind name. Never the payload. */
  token: string;
  /** Extra breadcrumbs for the telemetry sink. Never rendered in production. */
  context?: Record<string, string | number | boolean | null | undefined>;
}

export function UnknownTurn({ leakKind, token, context }: UnknownTurnProps) {
  useEffect(() => {
    reportLeak({ kind: leakKind, token, context });
  }, [leakKind, token, context]);

  if (import.meta.env.DEV) {
    return (
      <div
        data-leak-marker={leakKind}
        data-leak-token={token}
        className="mb-[18px] overflow-hidden rounded-lg border-2 border-alarm bg-alarm text-white"
      >
        <div className="flex items-center gap-2 bg-alarm px-3 py-1.5 font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.18em]">
          <svg aria-hidden viewBox="0 0 12 12" className="h-3 w-3 fill-current">
            <path d="M6 0 12 11H0z" />
          </svg>
          Leak guard tripped
        </div>
        <div className="bg-white px-3 py-2.5 font-mono text-[0.8125rem] text-alarm">
          <div>
            <span className="opacity-60">kind</span> {leakKind}
          </div>
          <div>
            <span className="opacity-60">token</span> {token}
          </div>
          {context ? (
            <div className="mt-1 opacity-70">
              {Object.entries(context)
                .map(([k, v]) => `${k}=${String(v)}`)
                .join('  ')}
            </div>
          ) : null}
          <p className="mt-2 max-w-prose whitespace-normal font-sans text-[0.8125rem] leading-snug text-graphite-700">
            Something reached the client surface that this dashboard has no mapping for. The server projection is an
            allowlist, so this is contract drift or a scrub bug — not a kind to render optimistically. It has been
            reported.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-leak-marker={leakKind}
      className="mb-[18px] rounded-lg bg-bone px-[22px] py-4 text-[0.90625rem] text-graphite-500"
    >
      Part of this update could not be shown. Nothing has been lost — we have been notified.
    </div>
  );
}
